import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { Instagram, Facebook, Mail, Phone, MapPin, Clock } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { useSiteInfo } from "../context/SiteInfoContext";
import { toast } from "sonner";

export default function Footer() {
  const { info } = useSiteInfo();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [pages, setPages] = useState([]);

  useEffect(() => {
    api.get("/pages").then(({ data }) => setPages(data)).catch(() => {});
  }, []);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await api.post("/newsletter", { email });
      toast.success("Welcome to Aura & Co. Check your inbox for 10% off your first order.");
      setEmail("");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };

  const socials = [
    { url: info.instagram, Icon: Instagram, label: "Instagram" },
    { url: info.facebook, Icon: Facebook, label: "Facebook" },
  ].filter((s) => s.url);

  // Static "care" links + custom pages appended
  const careLinks = [
    { to: "/track", label: "Track an Order" },
    { to: "/account", label: "My Account" },
    { to: "/journal", label: "Journal" },
    ...pages.map((p) => ({ to: `/pages/${p.slug}`, label: p.title })),
    { href: `mailto:${info.email}`, label: "Contact" },
  ];

  return (
    <footer className="bg-cream border-t border-hair mt-24" data-testid="footer">
      <div className="max-w-[1480px] mx-auto px-6 md:px-10 py-20 grid md:grid-cols-12 gap-14">
        <div className="md:col-span-5">
          <p className="eyebrow text-neutral-600">The Letter</p>
          <h3 className="font-serif text-3xl md:text-4xl mt-4 mb-6 leading-tight">
            Receive 10% off your first order, and the inside view from our atelier.
          </h3>
          <form onSubmit={subscribe} className="flex border-b border-neutral-900 pb-2 max-w-md mb-10" data-testid="newsletter-form">
            <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="Your email address" data-testid="newsletter-email"
              className="flex-1 bg-transparent border-none outline-none text-sm" />
            <button disabled={loading} className="text-[12px] tracking-[0.22em] uppercase font-medium hover:text-[var(--gold)] transition-colors" data-testid="newsletter-submit">
              {loading ? "…" : "Subscribe"}
            </button>
          </form>
          {socials.length > 0 && (
            <div className="flex gap-4" data-testid="footer-socials">
              {socials.map(({ url, Icon, label }) => (
                <a key={label} href={url} target="_blank" rel="noreferrer" aria-label={label}
                   className="text-neutral-700 hover:text-[var(--gold)] transition-colors">
                  <Icon strokeWidth={1.25} className="w-5 h-5" />
                </a>
              ))}
            </div>
          )}
        </div>

        <div className="md:col-span-7 grid grid-cols-2 md:grid-cols-3 gap-10 text-sm font-light text-neutral-700">
          <div>
            <p className="eyebrow text-neutral-900 mb-4">Boutique</p>
            <ul className="space-y-3">
              <li><Link to="/shop">Shop All</Link></li>
              <li><Link to="/category/rings">Rings</Link></li>
              <li><Link to="/category/necklaces">Necklaces</Link></li>
              <li><Link to="/category/earrings">Earrings</Link></li>
              <li><Link to="/category/bracelets">Bracelets</Link></li>
              <li><Link to="/category/others">Others</Link></li>
            </ul>
          </div>
          <div data-testid="footer-care">
            <p className="eyebrow text-neutral-900 mb-4">Care</p>
            <ul className="space-y-3">
              {careLinks.map((l, i) => (
                <li key={i}>
                  {l.href ? <a href={l.href}>{l.label}</a> : <Link to={l.to} data-testid={`care-link-${l.to?.replace(/\//g,'-')}`}>{l.label}</Link>}
                </li>
              ))}
            </ul>
          </div>
          <div data-testid="footer-contact">
            <p className="eyebrow text-neutral-900 mb-4">Visit & Contact</p>
            <ul className="space-y-3">
              {info.address_lines?.length > 0 && (
                <li className="flex gap-2">
                  <MapPin strokeWidth={1.25} className="w-4 h-4 mt-0.5 text-[var(--gold)] flex-shrink-0" />
                  <span>{info.address_lines.map((l, i) => <span key={i} className="block">{l}</span>)}</span>
                </li>
              )}
              {info.phone && (
                <li className="flex gap-2 items-center">
                  <Phone strokeWidth={1.25} className="w-4 h-4 text-[var(--gold)]" />
                  <a href={`tel:${info.phone.replace(/\s+/g, "")}`}>{info.phone}</a>
                </li>
              )}
              {info.email && (
                <li className="flex gap-2 items-center">
                  <Mail strokeWidth={1.25} className="w-4 h-4 text-[var(--gold)]" />
                  <a href={`mailto:${info.email}`}>{info.email}</a>
                </li>
              )}
              {info.hours && (
                <li className="flex gap-2 items-center">
                  <Clock strokeWidth={1.25} className="w-4 h-4 text-[var(--gold)]" />
                  <span>{info.hours}</span>
                </li>
              )}
            </ul>
          </div>
        </div>
      </div>
      <div className="border-t border-hair">
        <div className="max-w-[1480px] mx-auto px-6 md:px-10 py-6 flex flex-col md:flex-row justify-between gap-3 text-[11px] tracking-[0.22em] uppercase text-neutral-500">
          <span>© {new Date().getFullYear()} {info.brand_name?.replace("·", " & ")}. London</span>
          {info.vat_number && <span>VAT {info.vat_number} · Hatton Garden</span>}
        </div>
      </div>
    </footer>
  );
}
